import * as Sentry from '@sentry/react';
import { getProcessEnv } from 'utils/fnEnvironments';
import { captureException, isNetworkFailure } from 'utils/fnError';
import { getBackoffMs, isRetryableStatus, wait } from 'utils/fnBackoff';
import { ConfigError, ConfigResponse } from 'types/Config';
import { getLocalStorage } from 'utils/fnStorage';

const configApiKey = '117a3f89a361d27ad13549f47a672431704cc30d2109eff444d0f1ea09e64ece';

const CONFIG_ATTEMPTS = 3;
const TIMEOUT_STEPS = [5000, 10000, 15000];
const BOOT_DEADLINE_MS = 20000;
const ONLINE_WAIT_MS = 10000;
const MIN_USEFUL_ATTEMPT_MS = 1000;
const UNPROCESSABLE_ENTITY = 422;

type ConfigResourceName = 'config' | 'translations';

export type ConfigRequestOptions = {
    attempts?: number;
    deadlineMs?: number;
};

const commonHeaders = {
    acceptAllHeader: {
        Accept: '*/*',
    },
    acceptJsonHeader: {
        Accept: 'application/json',
    },
    contentTypeHeader: {
        'Content-Type': 'application/json',
    },
    configHeaders: {
        'x-operating-system': 'browser',
        'x-device-type': 'generic',
        Authorization: `Bearer ${configApiKey}`,
    },
};

const fullUrl = (path: string = ''): string => {
    let endpoint = getProcessEnv('CONFIG_ENDPOINT', '');

    if (endpoint.slice(-1) === '/') {
        endpoint = endpoint.slice(0, -1);
    }

    if (path !== '' && path.slice(0, 1) !== '/') {
        path = `/${path}`;
    }

    return `${endpoint}${path}/v2`;
};

const defaultErrorResponseParser = async (response: UnPromisify<ReturnType<typeof fetch>>): Promise<ConfigError> => ({
    code: response.status,
    msg: response.statusText,
});

const reportConfigError = (error, resource: ConfigResourceName, attempts: number) => {
    console.error(`loading remote ${resource} failed after ${attempts} attempt(s)`, error);

    if (!captureException(error) || window.navigator.onLine === false) {
        return;
    }

    Sentry.captureException(error, {
        tags: { configResource: resource },
        extra: { attempts },
        fingerprint: ['config-service', resource],
    });
};

type UnPromisify<T> = T extends Promise<infer U> ? U : T;

type FetchInit = Parameters<typeof fetch>[1];

type ParsingRules<T> = {
    [key: number]: (response: UnPromisify<ReturnType<typeof fetch>>) => T | Promise<T>;
};

type AttemptResult<T> = {
    ok: boolean;
    value?: T;
    error?: any;
    retryable?: boolean;
};

export class ConfigTimeoutError extends Error {
    constructor(resource: ConfigResourceName, timeoutMs: number) {
        super(`Timed out after ${timeoutMs}ms loading remote ${resource}`);
        this.name = 'ConfigTimeoutError';
    }
}

const getConfigServiceHeaders = () => {
    const configVersion = getLocalStorage('configVersion');
    let headers = commonHeaders.configHeaders;
    // eslint-disable-next-line no-underscore-dangle
    const version = configVersion?.length ? configVersion : window.__VERSION__?.replace('-', '.');

    if (version && version.length) {
        headers = {
            ...headers,
            ...{
                'x-operating-system-version': version,
                'x-client-app-version': version,
            },
        };
    }

    return headers;
};

const addEnvironment = (url: string): string => {
    if (!url) return null;

    const environment = getProcessEnv('CONFIG_ENV', 'development');
    return `${url}?customFilters=environment[${environment}]`;
};

const awaitOnline = (maxWaitMs: number): Promise<void> =>
    new Promise<void>((resolve) => {
        if (window.navigator.onLine !== false) {
            resolve();
            return;
        }

        let timer = 0;

        const done = () => {
            window.clearTimeout(timer);
            window.removeEventListener('online', done);
            resolve();
        };

        timer = window.setTimeout(done, maxWaitMs);
        window.addEventListener('online', done);
    });

const withAttemptCount = (error: any, attemptsMade: number) => {
    if (error && typeof error === 'object' && !('configAttempts' in error)) {
        error.configAttempts = attemptsMade;
    }

    return error;
};

// fetch rejects with TypeError for every network-class failure, which catches the Safari DNS and CORS
// wording that the message list in fnError deliberately does not match
const isTransportFailure = (error: any): boolean => error instanceof TypeError || isNetworkFailure(error);

const runAttempt = async <T>(
    url: string,
    init: FetchInit,
    parsingRules: ParsingRules<T>,
    timeoutMs: number,
    resource: ConfigResourceName,
    attempt: number
): Promise<AttemptResult<T>> => {
    const controller = new AbortController();
    let timedOut = false;
    // the timer has to outlive the fetch itself: a response whose headers arrive promptly but whose
    // body then stalls would otherwise hang the parse below forever
    const id = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);

    try {
        const serverResponse = await fetch(url, { ...init, signal: controller.signal });

        if (serverResponse.status in parsingRules) {
            if (serverResponse.status === UNPROCESSABLE_ENTITY) {
                reportConfigError(
                    new Error(`Received unexpected status code: ${serverResponse.status} ${serverResponse.statusText}`),
                    resource,
                    attempt
                );
            }

            return { ok: true, value: await parsingRules[serverResponse.status](serverResponse) };
        }

        const statusError = new Error(`Received unexpected status code: ${serverResponse.status} ${serverResponse.statusText}`);

        serverResponse.body?.cancel().catch(() => {});

        return { ok: false, error: statusError, retryable: isRetryableStatus(serverResponse.status) };
    } catch (error) {
        if (timedOut) {
            return { ok: false, error: new ConfigTimeoutError(resource, timeoutMs), retryable: true };
        }

        return { ok: false, error, retryable: isTransportFailure(error) };
    } finally {
        window.clearTimeout(id);
    }
};

const requestConfigResource = async <T>(
    url: string,
    init: FetchInit,
    parsingRules: ParsingRules<T>,
    resource: ConfigResourceName,
    { attempts = CONFIG_ATTEMPTS, deadlineMs = BOOT_DEADLINE_MS }: ConfigRequestOptions = {}
): Promise<T> => {
    const requestUrl = addEnvironment(url);

    if (!requestUrl) {
        throw new Error(`Missing ${resource} url`);
    }

    const startedAt = Date.now();
    // every wait below is clamped to what is left, otherwise the budget only gets consulted between
    // attempts and cannot stop one that is already overrunning it
    const remainingMs = () => (deadlineMs > 0 ? deadlineMs - (Date.now() - startedAt) : Number.MAX_SAFE_INTEGER);
    let attemptsMade = 0;
    let lastError;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        // an attempt is only worth starting if enough budget is left for it to mean anything - sleeping
        // out the remainder and then giving up would just be dead air
        if (remainingMs() < MIN_USEFUL_ATTEMPT_MS) {
            break;
        }

        if (attempt > 0) {
            const backoffMs = getBackoffMs(attempt - 1);

            if (remainingMs() - backoffMs < MIN_USEFUL_ATTEMPT_MS) {
                break;
            }

            // eslint-disable-next-line no-await-in-loop
            await wait(backoffMs);
        }

        // eslint-disable-next-line no-await-in-loop
        await awaitOnline(Math.min(ONLINE_WAIT_MS, remainingMs() - MIN_USEFUL_ATTEMPT_MS));

        if (remainingMs() < MIN_USEFUL_ATTEMPT_MS) {
            break;
        }

        attemptsMade += 1;

        // eslint-disable-next-line no-await-in-loop
        const result = await runAttempt(
            requestUrl,
            init,
            parsingRules,
            Math.min(TIMEOUT_STEPS[Math.min(attempt, TIMEOUT_STEPS.length - 1)], remainingMs()),
            resource,
            attemptsMade
        );

        if (result.ok) {
            return result.value;
        }

        if (!result.retryable) {
            throw withAttemptCount(result.error, attemptsMade);
        }

        lastError = result.error;
    }

    throw withAttemptCount(lastError ?? new Error(`Gave up loading remote ${resource}`), attemptsMade);
};

export const downloadConfig = async (options: ConfigRequestOptions = {}): Promise<ConfigResponse | ConfigError | any> => {
    try {
        return await requestConfigResource<ConfigResponse | ConfigError>(
            fullUrl(),
            {
                method: 'GET',
                headers: {
                    ...commonHeaders.contentTypeHeader,
                    ...commonHeaders.acceptJsonHeader,
                    ...getConfigServiceHeaders(),
                },
            },
            {
                200: async (response) => (await response.json()) as ConfigResponse,
                401: defaultErrorResponseParser,
                422: defaultErrorResponseParser,
            },
            'config',
            options
        );
    } catch (error) {
        reportConfigError(error, 'config', error?.configAttempts ?? 0);
        throw error;
    }
};

export const downloadTranslations = async (url, forceReload = false, options: ConfigRequestOptions = {}): Promise<any> => {
    try {
        return await requestConfigResource<ConfigResponse | ConfigError>(
            url,
            {
                method: 'GET',
                headers: {
                    ...commonHeaders.contentTypeHeader,
                    ...commonHeaders.acceptJsonHeader,
                    ...getConfigServiceHeaders(),
                },
                cache: forceReload ? 'reload' : 'default',
            },
            {
                200: async (response) => (await response.json()) as ConfigResponse,
                401: defaultErrorResponseParser,
                422: defaultErrorResponseParser,
            },
            'translations',
            options
        );
    } catch (error) {
        console.error('loading remote translations failed, falling back to bundled translations', error);
        throw error;
    }
};
